import { useIntl } from 'react-intl';
import React, { useState } from 'react';
import { hot } from 'react-hot-loader/root';
import styled, { ThemeProvider } from 'styled-components';
import { useTitleFromPromise } from '@cld/console-apps-services';
import { NotFound } from '@cld/empty-state';
import GlobalStyles from '@cld/global-style';
import { LanguageProvider } from '@cld/intl';
import Progress from '@cld/progress';
import theme, { generateTheme } from '@cld/theme-helper';
import useCloudinaryFavicon from '@cld/use-cloudinary-favicon';

import { usePreviewNavigation } from '../common/hooks/usePreviewNavigation';
import { useScrollToElement } from '../common/hooks/useScrollToElement';
import { messages, loadMessages } from '../i18n';
import { useSetCollectionOpen } from '../common/hooks/useSetCollectionOpen';
import { DataProvider } from './data/DataProvider';
import { useData } from './data/useData';
import { Preview } from './Preview/Preview';
import { CollectionGrid } from './AssetGrid/CollectionGrid';
import { CollectionStateProvider } from './CollectionStateProvider';
import { MainLayout } from './MainLayout';
import { Navigation } from './Navigation/Navigation';

// A function that returns a header element of a collection of given index. For each collection we render header and collection grid
export const getCollectionScrollTarget = (scrollContainer, index) => scrollContainer.children[index * 2];

const Root = styled.div`
  background-color: ${theme`color.white`};
`;

const ProgressWrapper = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export const BrandPortalContent = ({ collections }) => {
  const [activeCollectionIndex, setActiveCollectionIndex] = useState(0);
  const notEmptyCollections = collections ? collections.filter((collection) => collection.assets.length > 0) : [];
  const { ref } = useScrollToElement(collections && activeCollectionIndex, getCollectionScrollTarget);
  const { canNavigateToNext, canNavigateToPrevious, handleClose, handleNext, handlePrevious, previewedAsset, setPreview } = usePreviewNavigation(
    notEmptyCollections
  );
  const setOpen = useSetCollectionOpen();

  return (
    <Root>
      <MainLayout
        contentRef={ref}
        navigation={
          collections && (
            <Navigation
              onClick={(index) => {
                setOpen(notEmptyCollections[index].id, true);
                setActiveCollectionIndex(index);
              }}
              selectedIndex={activeCollectionIndex}
              items={notEmptyCollections.map((x) => ({ value: x.name, id: x.id }))}
            />
          )
        }
        content={
          collections &&
          notEmptyCollections.map((collection, collectionIndex) => {
            const handleSetPreviewedAssetIndex = (newPreviewedAssetIndex) => setPreview(collectionIndex, newPreviewedAssetIndex);

            return <CollectionGrid key={collection.id} collection={collection} setPreviewedAssetIndex={handleSetPreviewedAssetIndex} />;
          })
        }
      />

      {previewedAsset && (
        <Preview
          canNavigateToNext={canNavigateToNext}
          canNavigateToPrevious={canNavigateToPrevious}
          onClose={handleClose}
          onNext={handleNext}
          onPrevious={handlePrevious}
          previewedAsset={previewedAsset}
        />
      )}
    </Root>
  );
};

const BrandPortal = () => {
  const { formatMessage } = useIntl();
  const notFoundMessage = formatMessage(messages.portalNotFound);

  const { info, collections } = useData();

  useTitleFromPromise(info, (info) => info.name, notFoundMessage);
  useCloudinaryFavicon();

  if (info.isLoading || collections.isLoading) {
    return (
      <ProgressWrapper>
        <Progress.Circular variant="primary" size="xl" />
      </ProgressWrapper>
    );
  }

  if (info.error || collections.error) {
    console.warn(info.error, collections.error);
    return <NotFound title={notFoundMessage} />;
  }

  return <BrandPortalContent collections={collections.data} />;
};

const App = () => {
  return (
    <ThemeProvider theme={generateTheme()}>
      <DataProvider>
        <CollectionStateProvider>
          <GlobalStyles />
          <LanguageProvider loadMessages={loadMessages}>
            <BrandPortal />
          </LanguageProvider>
        </CollectionStateProvider>
      </DataProvider>
    </ThemeProvider>
  );
};

export default hot(App);
